import toast from 'react-hot-toast';

/**
 * Serviço de tratamento de erros centralizado
 */

/**
 * Mapeia erros do Firebase para mensagens amigáveis
 */
const errorMessages = {
  // Auth errors
  'auth/user-not-found': 'Usuário não encontrado',
  'auth/wrong-password': 'Senha incorreta',
  'auth/email-already-in-use': 'Este email já está cadastrado',
  'auth/weak-password': 'Senha muito fraca. Use pelo menos 6 caracteres',
  'auth/invalid-email': 'Email inválido',
  'auth/operation-not-allowed': 'Operação não permitida',
  'auth/account-exists-with-different-credential': 'Já existe uma conta com este email',
  'auth/invalid-credential': 'Credenciais inválidas',
  'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde',
  
  // Firestore errors
  'permission-denied': 'Você não tem permissão para realizar esta ação',
  'not-found': 'Documento não encontrado',
  'already-exists': 'Este documento já existe',
  'failed-precondition': 'Operação cancelada: condição prévia não atendida',
  'unavailable': 'Serviço temporariamente indisponível. Tente novamente',
  'unauthenticated': 'Você precisa estar logado para realizar esta ação',
  
  // Network errors
  'network-request-failed': 'Erro de conexão. Verifique sua internet',
  'timeout': 'A operação demorou muito. Tente novamente',
  
  // Generic
  'unknown': 'Ocorreu um erro inesperado. Tente novamente'
};

/**
 * Obtém mensagem amigável para um erro
 */
export const getErrorMessage = (error) => {
  if (!error) return errorMessages.unknown;
  
  // Se o erro já tem uma mensagem customizada
  if (typeof error === 'string') return error;
  
  // Extrair código do erro do Firebase
  const errorCode = error.code || error.message || '';
  
  // Procurar por mensagem específica
  for (const [key, message] of Object.entries(errorMessages)) {
    if (errorCode.includes(key)) {
      return message;
    }
  }
  
  // Mensagem padrão
  return errorMessages.unknown;
};

/**
 * Mostra toast de erro com mensagem amigável
 */
export const showError = (error, customMessage = null) => {
  const message = customMessage || getErrorMessage(error);
  toast.error(message);
  console.error('Error:', error); // Log para debug
};

/**
 * Mostra toast de sucesso
 */
export const showSuccess = (message) => {
  toast.success(message);
};

/**
 * Wrapper para funções async que mostra erro automaticamente
 */
export const withErrorHandling = async (fn, errorMessage = null) => {
  try {
    return await fn();
  } catch (error) {
    showError(error, errorMessage);
    throw error; // Re-throw para permitir tratamento adicional
  }
};

/**
 * Valida campos obrigatórios antes de executar operação
 */
export const validateRequired = (fields, fieldNames) => {
  const missing = [];
  
  fields.forEach((field, index) => {
    if (!field || (typeof field === 'string' && field.trim() === '')) {
      missing.push(fieldNames[index]);
    }
  });
  
  if (missing.length > 0) {
    throw new Error(`Campos obrigatórios não preenchidos: ${missing.join(', ')}`);
  }
};

/**
 * Retry logic para operações que podem falhar temporariamente
 */
export const retryOperation = async (fn, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      console.warn(`Attempt ${attempt} failed:`, error);
      
      if (attempt < maxRetries) {
        // Esperar antes de tentar novamente (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }
  
  throw lastError;
};

/**
 * Valida conexão com internet
 */
export const checkConnection = () => {
  if (!navigator.onLine) {
    showError('Sem conexão com a internet');
    return false;
  }
  return true;
};

const errorHandler = {
  getErrorMessage,
  showError,
  showSuccess,
  withErrorHandling,
  validateRequired,
  retryOperation,
  checkConnection
};

export default errorHandler;
